import type { ComponentType } from "react";
import { lazy, Suspense } from "react";
import { createBrowserRouter, useRouteError } from "react-router-dom";
import App from "@/App";
import Spinner from "@/components/Spinner";
import MainLayout from "@/layouts/MainLayout";
import RootLayout from "@/layouts/RootLayout";
import Home from "@/pages/Home";

const AdminSignIn = lazy(() => import("@/pages/AdminSignIn"));
const Archived = lazy(() => import("@/pages/Archived"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Explore = lazy(() => import("@/pages/Explore"));
const Inboxes = lazy(() => import("@/pages/Inboxes"));
const JournalDetail = lazy(() => import("@/pages/JournalDetail"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PermissionDenied = lazy(() => import("@/pages/PermissionDenied"));
const Attachments = lazy(() => import("@/pages/Attachments"));
const Setting = lazy(() => import("@/pages/Setting"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const SignUp = lazy(() => import("@/pages/SignUp"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const JournalDetailRedirect = lazy(() => import("./JournalDetailRedirect"));
const TickerList = lazy(() => import("@/pages/TickerList"));
const TickerDetail = lazy(() => import("@/pages/TickerDetail"));
const TestDashboard = lazy(() => import("@/pages/TestDashboard"));
const TestErrorHandling = lazy(() => import("@/pages/TestErrorHandling"));
const TestErrorBoundaryPage = lazy(() => import("@/pages/TestErrorBoundaryPage"));
const TestEditor = lazy(() => import("@/pages/TestEditor"));
const TestSimple = lazy(() => import("@/pages/TestSimple"));
const TestStockAPI = lazy(() => import("@/pages/TestStockAPI"));
const TestLLMPrompt = lazy(() => import("@/pages/TestLLMPrompt"));
const TestAIRender = lazy(() => import("@/pages/TestAIRender"));
const TestValuationChart = lazy(() => import("@/pages/TestValuationChart"));
const TestLongportAPI = lazy(() => import("@/pages/TestLongportAPI"));
const SelfContracts = lazy(() => import("@/pages/SelfContracts"));
const ChecklistTemplates = lazy(() => import("@/pages/ChecklistTemplates"));

import { ROUTES } from "./routes";

// Backward compatibility alias
export const Routes = ROUTES;
export { ROUTES };

/**
 * Error component for lazy-loaded route failures
 * Handles cases where JavaScript chunks fail to load (e.g., backend is down)
 */
function LazyRouteErrorBoundary() {
  const error = useRouteError() as { message?: string };

  // Check if this is a dynamic import failure
  const isDynamicImportError = error?.message?.includes("Failed to fetch dynamically imported module");

  if (isDynamicImportError) {
    return (
      <div className="w-full h-64 flex flex-col items-center justify-center gap-4 p-4">
        <div className="text-center space-y-2">
          <h2 className="text-lg font-semibold text-foreground">Unable to load page</h2>
          <p className="text-sm text-muted-foreground">
            The page content could not be loaded. This might be due to a network issue or the server being temporarily unavailable.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Fallback for other errors
  return (
    <div className="w-full h-64 flex flex-col items-center justify-center gap-4 p-4">
      <div className="text-center space-y-2">
        <h2 className="text-lg font-semibold text-destructive">Something went wrong</h2>
        <p className="text-sm text-muted-foreground">{error?.message || "An unexpected error occurred while loading this page."}</p>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Reload Page
      </button>
    </div>
  );
}

// Helper component to reduce Suspense boilerplate for lazy routes
const LazyRoute = ({ component: Component }: { component: ComponentType }) => (
  <Suspense
    fallback={
      <div className="w-full h-64 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    }
  >
    <Component />
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        path: Routes.AUTH,
        children: [
          { path: "", element: <LazyRoute component={SignIn} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "admin", element: <LazyRoute component={AdminSignIn} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "signup", element: <LazyRoute component={SignUp} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "callback", element: <LazyRoute component={AuthCallback} />, errorElement: <LazyRouteErrorBoundary /> },
        ],
      },
      {
        path: Routes.ROOT,
        element: <RootLayout />,
        children: [
          {
            element: <MainLayout />,
            children: [
              { path: "", element: <Home /> },
              { path: Routes.EXPLORE, element: <LazyRoute component={Explore} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: Routes.ARCHIVED, element: <LazyRoute component={Archived} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "u/:username", element: <LazyRoute component={UserProfile} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: Routes.TICKERS, element: <LazyRoute component={TickerList} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "actions", element: <LazyRoute component={SelfContracts} />, errorElement: <LazyRouteErrorBoundary /> },
              {
                path: Routes.CHECKLIST_TEMPLATES,
                element: <LazyRoute component={ChecklistTemplates} />,
                errorElement: <LazyRouteErrorBoundary />,
              },
              { path: "test-dashboard", element: <LazyRoute component={TestDashboard} />, errorElement: <LazyRouteErrorBoundary /> },
              {
                path: "test-error-handling",
                element: <LazyRoute component={TestErrorHandling} />,
                errorElement: <LazyRouteErrorBoundary />,
              },
              {
                path: "test-error-boundary",
                element: <LazyRoute component={TestErrorBoundaryPage} />,
                errorElement: <LazyRouteErrorBoundary />,
              },
              { path: "test-stockapi", element: <LazyRoute component={TestStockAPI} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "test-longport", element: <LazyRoute component={TestLongportAPI} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "test-simple", element: <LazyRoute component={TestSimple} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "test-editor", element: <LazyRoute component={TestEditor} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "test-llmprompt", element: <LazyRoute component={TestLLMPrompt} />, errorElement: <LazyRouteErrorBoundary /> },
              { path: "test-airender", element: <LazyRoute component={TestAIRender} />, errorElement: <LazyRouteErrorBoundary /> },
              {
                path: "test-valuationchart",
                element: <LazyRoute component={TestValuationChart} />,
                errorElement: <LazyRouteErrorBoundary />,
              },
            ],
          },
          { path: Routes.ATTACHMENTS, element: <LazyRoute component={Attachments} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: Routes.INBOX, element: <LazyRoute component={Inboxes} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: Routes.SETTING, element: <LazyRoute component={Setting} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: Routes.TICKER_DETAIL, element: <LazyRoute component={TickerDetail} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "journals/:uid", element: <LazyRoute component={JournalDetail} />, errorElement: <LazyRouteErrorBoundary /> },
          // Redirect old path to new path
          { path: "m/:uid", element: <LazyRoute component={JournalDetailRedirect} />, errorElement: <LazyRouteErrorBoundary /> },
          // Legacy memos path redirect
          { path: "memos/:uid", element: <LazyRoute component={JournalDetailRedirect} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "403", element: <LazyRoute component={PermissionDenied} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "404", element: <LazyRoute component={NotFound} />, errorElement: <LazyRouteErrorBoundary /> },
          { path: "*", element: <LazyRoute component={NotFound} />, errorElement: <LazyRouteErrorBoundary /> },
        ],
      },
    ],
  },
]);

export default router;
